import { useState, useEffect } from "react";
import api, { API } from "@/api/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileText, FileSpreadsheet, Download, Send, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * LogReportDialog — Email + Download report dialog for Inward/Outward Log.
 * Supports Summary and Detailed report types in CSV/Excel/PDF; email uses server-side sender.
 * Remembers last recipients in localStorage.
 */
export default function LogReportDialog({ open, onClose, direction, logId = null, manifestMode = false }) {
  const { user } = useAuth();
  const LSKEY = `log-report-${direction}`;
  const [mode, setMode] = useState("download"); // "download" | "email"
  const [reportType, setReportType] = useState(manifestMode ? "detailed" : "summary");
  const [format, setFormat] = useState("pdf");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [branchId, setBranchId] = useState("");
  const [courier, setCourier] = useState("");
  const [lstatus, setLstatus] = useState("");
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!open) return;
    const saved = JSON.parse(localStorage.getItem(LSKEY) || "{}");
    setTo(saved.to || user?.email || "");
    setCc(saved.cc || "");
    setBcc(saved.bcc || "");
    setSubject(`${direction === "inward" ? "Inward" : "Outward"} Log Report`);
    setMessage("Please find the attached report.");
    setSummary(null);
    // Daily Inward Summary — fetch counts for the manifest header box
    if (manifestMode && logId && direction === "inward") {
      api.get(`/inward-logs/${logId}/summary`).then(r => setSummary(r.data)).catch(() => setSummary(null));
    }
    // eslint-disable-next-line
  }, [open, logId, manifestMode, direction]);

  const filters = () => {
    const p = { report_type: reportType, format };
    if (logId) p.log_id = logId;
    if (start) p.start = start;
    if (end) p.end = end;
    if (branchId) p.branch_id = branchId;
    if (courier) p.courier = courier;
    if (lstatus) p.lstatus = lstatus;
    return p;
  };

  const doDownload = async () => {
    setBusy(true);
    try {
      const p = filters();
      const { data } = await api.get(`/reports/logs/${direction}/export`, { params: p, responseType: "blob" });
      const ext = format === "xlsx" || format === "excel" ? "xlsx" : format;
      const blob = new Blob([data], { type: data.type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${direction}-log-${reportType}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Report downloaded (${format.toUpperCase()})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    } finally { setBusy(false); }
  };

  const doEmail = async () => {
    if (!to.trim()) { toast.error("At least one recipient is required"); return; }
    setBusy(true);
    try {
      const body = { ...filters(), to, cc, bcc, subject, message };
      const { data } = await api.post(`/reports/logs/${direction}/email`, body);
      localStorage.setItem(LSKEY, JSON.stringify({ to, cc, bcc }));
      toast.success(`Report emailed to ${(data.sent_to || []).length} recipient(s) · ${data.row_count} rows`);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email failed");
    } finally { setBusy(false); }
  };

  const submit = () => (mode === "download" ? doDownload() : doEmail());

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="rounded-md max-w-xl max-h-[90vh] overflow-y-auto" data-testid={`logrep-dialog-${direction}`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {direction === "inward" ? "Inward" : "Outward"} Log · Report
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          {/* Daily Inward Summary — manifest header (Feb 2026 · client meeting) */}
          {summary && (
            <div className="border border-primary/40 bg-red-50/40 rounded-md p-3" data-testid="manifest-summary-box">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex flex-col">
                  <span className="overline">Daily Inward Summary</span>
                  <span className="mono text-[11px] text-muted-foreground">
                    Log {summary.log_no} · {summary.branch_name || "—"} · {(summary.created_at || "").slice(0,10)}
                  </span>
                </div>
                <span className="mono text-[10px] text-muted-foreground uppercase tracking-widest">
                  {summary.courier || "—"} · {summary.delivery_staff || "—"}
                </span>
              </div>
              <div className="mt-3 grid grid-cols-3 md:grid-cols-6 gap-2" data-testid="manifest-summary-grid">
                {[
                  { key: "shipments", label: "Shipments", value: summary.counts.shipments },
                  { key: "accepted",  label: "Accepted",  value: summary.counts.accepted ?? 0, tone: "ok" },
                  { key: "packages",  label: "Packages",  value: summary.counts.packages },
                  { key: "weight",    label: "Weight (kg)", value: Number(summary.counts.weight_kg || 0).toFixed(3) },
                  { key: "delivered", label: "Delivered", value: summary.counts.delivered, tone: "ok" },
                  { key: "pending",   label: "Pending",   value: summary.counts.pending, tone: "warn" },
                  { key: "rto",       label: "RTO (intake)", value: summary.counts.intake_rto ?? summary.counts.rto, tone: "bad" },
                ].map(cell => (
                  <div key={cell.key} data-testid={`summary-cell-${cell.key}`}
                       className={`border rounded-md px-2 py-2 bg-card ${
                         cell.tone === "ok" ? "border-emerald-300" :
                         cell.tone === "warn" ? "border-amber-300" :
                         cell.tone === "bad" ? "border-red-400" : "border-border"}`}>
                    <div className="overline text-[9px]">{cell.label}</div>
                    <div className={`mono text-lg font-bold ${
                      cell.tone === "ok" ? "text-emerald-700" :
                      cell.tone === "warn" ? "text-amber-700" :
                      cell.tone === "bad" ? "text-red-700" : ""}`}>{cell.value}</div>
                  </div>
                ))}
              </div>
              {(summary.intake_rto_reasons || []).length > 0 && (
                <div className="mt-3 border border-red-300 bg-red-50/60 rounded-md p-2" data-testid="manifest-rto-reasons">
                  <div className="overline text-[9px] text-red-800">RTO items · reasons captured at intake</div>
                  <ul className="mt-1 space-y-0.5">
                    {summary.intake_rto_reasons.map((r, i) => (
                      <li key={i} className="text-[11px] mono text-red-900" data-testid={`manifest-rto-reason-${i}`}>
                        <b>{r.item}</b>{r.recipient !== "—" ? ` → ${r.recipient}` : ""} <span className="text-red-700">— {r.reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="text-[10px] text-muted-foreground mt-2">
                <b>Accepted / Delivered</b> are green, <b>Pending</b> is amber, <b>RTO</b> shows the intake-level rejects (reasons listed above).
              </div>
            </div>
          )}

          {/* Mode toggle */}
          <div className="inline-flex border border-border rounded-md overflow-hidden">
            {["download", "email"].map((m) => (
              <button key={m} data-testid={`logrep-mode-${m}`} onClick={() => setMode(m)}
                className={`px-4 py-1.5 text-xs uppercase tracking-widest ${mode === m ? "bg-primary text-primary-foreground" : ""}`}>
                {m === "download" ? <><Download size={12} className="inline mr-1" /> Download</> : <><Send size={12} className="inline mr-1" /> Email</>}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="overline">Report Type</span>
              <select data-testid="logrep-type" value={reportType} onChange={(e) => setReportType(e.target.value)}
                      className="mt-1 w-full h-9 px-2 rounded-md border border-input bg-background text-sm">
                <option value="summary">Summary</option>
                <option value="detailed">Detailed (with parcels)</option>
              </select>
            </label>
            <label className="block">
              <span className="overline">Format</span>
              <select data-testid="logrep-format" value={format} onChange={(e) => setFormat(e.target.value)}
                      className="mt-1 w-full h-9 px-2 rounded-md border border-input bg-background text-sm">
                <option value="pdf">PDF</option>
                <option value="xlsx">Excel (.xlsx)</option>
                <option value="csv">CSV</option>
              </select>
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="overline">Start Date</span>
              <Input data-testid="logrep-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 rounded-md" />
            </label>
            <label className="block">
              <span className="overline">End Date</span>
              <Input data-testid="logrep-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 rounded-md" />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {direction === "inward" && (
              <label className="block">
                <span className="overline">Courier</span>
                <Input data-testid="logrep-courier" value={courier} onChange={(e) => setCourier(e.target.value)} className="mt-1 rounded-md" placeholder="e.g. BlueDart" />
              </label>
            )}
            <label className="block">
              <span className="overline">Status</span>
              <select data-testid="logrep-status" value={lstatus} onChange={(e) => setLstatus(e.target.value)}
                      className="mt-1 w-full h-9 px-2 rounded-md border border-input bg-background text-sm">
                <option value="">All</option>
                <option value="open">Open</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </label>
          </div>

          {mode === "email" && (
            <div className="border-t border-border pt-3 space-y-3">
              <label className="block">
                <span className="overline">To (comma-separated) *</span>
                <Input data-testid="logrep-to" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 rounded-md font-mono" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="overline">CC</span>
                  <Input data-testid="logrep-cc" value={cc} onChange={(e) => setCc(e.target.value)} className="mt-1 rounded-md font-mono" />
                </label>
                <label className="block">
                  <span className="overline">BCC</span>
                  <Input data-testid="logrep-bcc" value={bcc} onChange={(e) => setBcc(e.target.value)} className="mt-1 rounded-md font-mono" />
                </label>
              </div>
              <label className="block">
                <span className="overline">Subject</span>
                <Input data-testid="logrep-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1 rounded-md" />
              </label>
              <label className="block">
                <span className="overline">Message</span>
                <textarea data-testid="logrep-message" value={message} onChange={(e) => setMessage(e.target.value)}
                          rows={3} className="mt-1 w-full rounded-md border border-input p-2 text-sm bg-background" />
              </label>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} data-testid="logrep-cancel" className="rounded-md"><X size={13} className="mr-1" /> Cancel</Button>
            <Button onClick={submit} disabled={busy} data-testid="logrep-submit" className="rounded-md bg-primary text-primary-foreground">
              {mode === "download" ? <><Download size={13} className="mr-1.5" /> {busy ? "Preparing…" : "Download"}</> :
                                     <><Send size={13} className="mr-1.5" /> {busy ? "Sending…" : "Send Email"}</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
